import { defineComponent as _defineComponent } from 'vue'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import type Vue from 'vue';
import { PriceItem } from '@/interfaces/general/PriceItem';
import { toggleClassBasedOnOverlapFromTargetElement } from '@/helpers/ScrollHelper';
import { debounce } from 'vue-debounce';
import { UserRepository } from '@/repositories/UserRepository';
import { UserRightsEnum } from '@/enums/global/UserRights';
import { formatNumberForDisplay } from '@/helpers/NumberFormatter';
import i18n from '@/i18n';
import store from '@/store';

/* === Props === */
interface Props {
    sellingPriceList: PriceItem[];
    purchasePriceList: PriceItem[];
    roltekPriceList: PriceItem[];
    shouldHandleIfOffersAreLoaded?: boolean;
    shouldRecalculatePosition?: boolean;
    overlappingElement?: Vue;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'PriceModule',
  props: {
    sellingPriceList: null,
    purchasePriceList: null,
    roltekPriceList: null,
    shouldHandleIfOffersAreLoaded: { type: Boolean, default: false },
    shouldRecalculatePosition: { type: Boolean, default: false },
    overlappingElement: { default: undefined }
  },
  setup(__props: any) {

const props = __props as { sellingPriceList: PriceItem[], purchasePriceList: PriceItem[], roltekPriceList: PriceItem[], shouldHandleIfOffersAreLoaded: boolean, shouldRecalculatePosition: boolean, overlappingElement: Vue };



/* === Price Display === */
// false = SellingPrice; true = PurchasePrice
const togglePrice = ref(false);
const userRights = computed(() => store.getters['jwtData/userRights']);
const canUserViewPurchasePrice = computed(() => userRights.value.includes(UserRightsEnum.VIEW_PURCHASE_PRICE));
const currentUserGroup = computed(() => String(store.getters['jwtData/currentUserGroup']));

const toggledPriceList = computed<PriceItem[]>(() => {
    if (togglePrice.value && props.purchasePriceList) {
        if (currentUserGroup.value == '1' || currentUserGroup.value == '2') {
            return props.roltekPriceList;
        } else {
            return props.purchasePriceList;
        }
    } else {
        if (currentUserGroup.value == '1' || currentUserGroup.value == '2') {
            return props.purchasePriceList;
        } else {
            return props.sellingPriceList;
        }
    }
});

const finalPurchasePrice = computed(() => {
    if (currentUserGroup.value == '1' || currentUserGroup.value == '2') {
        return props.roltekPriceList[props.roltekPriceList.length - 1];
    } else {
        if (!props.purchasePriceList || props.purchasePriceList.length === 0) {
            return null;
        }
        return props.purchasePriceList[props.purchasePriceList.length - 1];
    }
});

const finalSellingPrice = computed(() => {
    if (props.sellingPriceList.length === 0) {
        return null;
    }

    return props.sellingPriceList[props.sellingPriceList.length - 1];
});

const totalPrice = computed(() =>
    togglePrice.value
        ? finalPurchasePrice.value
            ? finalPurchasePrice.value.priceValue
            : ''
        : finalSellingPrice.value.priceValue,
);

const formattedTotalPrice = computed(() => formatNumberForDisplay(totalPrice.value, 2, 2));

function formatMoney(value: number | string | null | undefined) {
    return formatNumberForDisplay(value, 2, 2);
}

const priceTitle = computed(() => {
    const isPrivileged = currentUserGroup.value === '1' || currentUserGroup.value === '2';

    if (togglePrice.value) {
        return isPrivileged ? i18n.t('Ukupna kupovna cijena') : i18n.t('Ukupna prodajna cijena');
    } else {
        return isPrivileged ? i18n.t('Ukupna prodajna cijena') : i18n.t('Ukupna kupovna cijena');
    }
});

const priceLabel = computed(() => {
    const isPrivileged = currentUserGroup.value === '1' || currentUserGroup.value === '2';

    if (togglePrice.value) {
        return isPrivileged ? i18n.t('Pojedinosti o kupovnoj cijeni') : i18n.t('Pojedinosti o prodajnoj cijeni');
    } else {
        return isPrivileged ? i18n.t('Pojedinosti o prodajnoj cijeni') : i18n.t('Pojedinosti o kupovnoj cijeni');
    }
});

const shouldShowLoading = computed(() => {
    if (!props.shouldHandleIfOffersAreLoaded) {
        return false;
    }

    return store.getters['projectLoadingState/areOffersLoading'];
});

const currencySymbol = computed(() => UserRepository.getCurrentUsersCurrencySymbol());

/* === Overlap Handling === */
const priceModule = ref<HTMLElement | null>(null);
const debouncedHandleScroll = debounce(() => {
    handleScroll();
}, 40);

function handleScroll() {
    if (props.overlappingElement == null) {
        return;
    }

    const priceModuleElement = priceModule.value as HTMLElement;
    const overlappingElement = props.overlappingElement.$el as HTMLElement;

    toggleClassBasedOnOverlapFromTargetElement(priceModuleElement, overlappingElement, 'isOverlappingButton');
}

if (props.shouldRecalculatePosition) {
    window.addEventListener('scroll', debouncedHandleScroll);
}

onBeforeUnmount(() => {
    if (props.shouldRecalculatePosition) {
        window.removeEventListener('scroll', debouncedHandleScroll);
    }
});

/* === Price List Synchronization === */
watch(
    () => props.sellingPriceList,
    () => {
        if (props.sellingPriceList && props.sellingPriceList.length > 0) {
            nextTick(() => {
                handleScroll();
            });
        }
    },
    { deep: true },
);

return { __sfc: true,props, togglePrice, userRights, canUserViewPurchasePrice, currentUserGroup, toggledPriceList, finalPurchasePrice, finalSellingPrice, totalPrice, formattedTotalPrice, formatMoney, priceTitle, priceLabel, shouldShowLoading, currencySymbol, priceModule, debouncedHandleScroll, handleScroll }
}

})